
import { supabase } from '@/integrations/supabase/client';
import { Trip } from '@/types/itinerary';
import { User } from '@supabase/supabase-js';
import { toast } from 'sonner';

export const fetchUserTrips = async (user: User | null): Promise<Trip[]> => {
  if (!user) return [];
  
  console.log("Fetching user trips...");
  try {
    // Get trips created by the user
    const { data: createdTrips, error: createdError } = await supabase
      .from('trips')
      .select('*')
      .eq('owner_id', user.id)
      .order('created_at', { ascending: false });

    if (createdError) throw createdError;
    console.log("Owned trips fetched:", createdTrips?.length);

    // Get trips the user has joined as a member
    const { data: joinedTripsRaw, error: joinedError } = await supabase
      .from('trip_members')
      .select('trip_id')
      .eq('user_id', user.id);

    if (joinedError) throw joinedError;
    console.log("Joined trip IDs fetched:", joinedTripsRaw?.length);

    // If user has joined trips, fetch the full trip data
    let joinedTrips: Trip[] = [];
    if (joinedTripsRaw && joinedTripsRaw.length > 0) {
      // Filter out trip IDs that the user already owns to prevent duplicates
      const ownedTripIds = new Set(createdTrips?.map(trip => trip.id) || []);
      const uniqueJoinedTripIds = joinedTripsRaw
        .map(membership => membership.trip_id)
        .filter(id => !ownedTripIds.has(id));
      
      if (uniqueJoinedTripIds.length > 0) {
        const { data: fullJoinedTrips, error: fullJoinedError } = await supabase
          .from('trips')
          .select('*')
          .in('id', uniqueJoinedTripIds)
          .order('created_at', { ascending: false });
          
        if (fullJoinedError) throw fullJoinedError;
        joinedTrips = fullJoinedTrips || [];
        console.log("Joined trips fetched:", joinedTrips?.length);
      }
    }

    // Mark joined trips
    joinedTrips = joinedTrips.map(trip => ({
      ...trip,
      isJoined: true
    }));

    // Combine owned and joined trips
    const allTrips = [
      ...(createdTrips || []).map(trip => ({ ...trip, isJoined: false })),
      ...joinedTrips
    ];
    
    console.log("Total unique trips:", allTrips.length);
    return allTrips;
  } catch (error) {
    console.error('Error fetching trips:', error);
    toast.error('Failed to load your trips. Please try again.');
    return [];
  }
};
